/**
 * Settings Page
 *
 * App settings and user preferences with refined design
 */

import {
	APP_SETTINGS_KEY,
	effectiveThemeChoice,
	isNativeWebView,
	resolveAppTheme,
	type ThemeChoice,
} from '@polygraf/shared'
import { ExternalLink, Globe, Info, Monitor, Moon, Sun, User } from 'lucide-react'
import { forwardRef, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { BrandMark } from '../components/brand-mark'
import { buttonClasses, cn } from '../components/ui'
import { usePageTitle } from '../hooks/use-page-title'
import { useSession } from '../lib/auth-client'

interface AppSettings {
	theme: ThemeChoice
}

function readRawSettings(): string | null {
	try {
		return localStorage.getItem(APP_SETTINGS_KEY)
	} catch {
		return null
	}
}

/** The option the page is really using, so an unset theme is not shown as "system". */
function loadSettings(): AppSettings {
	return { theme: effectiveThemeChoice(readRawSettings(), isNativeWebView()) }
}

/**
 * Written on a choice only, never on mount: saving the displayed default would
 * turn "never chose" into an explicit choice and change the page on next load.
 */
function saveTheme(theme: ThemeChoice) {
	try {
		let stored: Record<string, unknown> = {}
		try {
			const parsed = JSON.parse(readRawSettings() ?? 'null')
			if (parsed && typeof parsed === 'object') stored = parsed
		} catch {
			// Unreadable payload: start over rather than keep garbage.
		}
		localStorage.setItem(APP_SETTINGS_KEY, JSON.stringify({ ...stored, theme }))
	} catch {
		// Storage blocked (privacy mode) — the choice applies until reload.
	}
}

export default function SettingsPage() {
	const { t } = useTranslation()
	usePageTitle(t('settings.title'))
	const { data: session } = useSession()
	const [settings, setSettings] = useState<AppSettings>(loadSettings)
	const { theme } = settings

	const handleThemeChange = (newTheme: ThemeChoice) => {
		setSettings((prev) => ({ ...prev, theme: newTheme }))
		saveTheme(newTheme)
		// Resolve through the shared resolver so the live attribute and the next
		// page load can never disagree.
		const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
		document.documentElement.setAttribute(
			'data-theme',
			resolveAppTheme(JSON.stringify({ theme: newTheme }), prefersDark, false),
		)
	}

	// Theme picker is a mutually-exclusive set → WAI-ARIA radiogroup semantics
	// (role=radiogroup + role=radio, roving tabindex, arrow-key navigation).
	const themeGroupLabelId = useId()
	const themeOptions: { value: AppSettings['theme']; icon: React.ReactNode; label: string }[] = [
		{ value: 'light', icon: <Sun size={18} />, label: t('settings.themeOptions.light') },
		{ value: 'dark', icon: <Moon size={18} />, label: t('settings.themeOptions.dark') },
		{ value: 'system', icon: <Monitor size={18} />, label: t('settings.themeOptions.system') },
	]
	const themeButtonRefs = useRef<(HTMLButtonElement | null)[]>([])

	// APG radio pattern: Arrow keys move focus to the adjacent radio (wrapping),
	// uncheck the old one and check the new one. Space/Enter still select via the
	// native button click. Selection always mirrors the focused radio here.
	const handleThemeKeyDown = (e: React.KeyboardEvent, index: number) => {
		let nextIndex: number | null = null
		if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
			nextIndex = (index + 1) % themeOptions.length
		} else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
			nextIndex = (index - 1 + themeOptions.length) % themeOptions.length
		}
		if (nextIndex === null) return
		e.preventDefault()
		handleThemeChange(themeOptions[nextIndex].value)
		themeButtonRefs.current[nextIndex]?.focus()
	}

	return (
		<div className="max-w-2xl mx-auto space-y-6 animate-enter">
			<div className="mb-8">
				<h1>{t('settings.title')}</h1>
				<p className="text-base-content/60 mt-2">{t('settings.subtitle')}</p>
			</div>

			{/* Appearance */}
			<div className="settings-card">
				<h2>
					<Sun size={20} className="text-coral-500" />
					<span>{t('settings.appearance')}</span>
				</h2>

				<div className="space-y-4">
					<div>
						<p id={themeGroupLabelId} className="block text-sm font-medium mb-3">
							{t('settings.theme')}
						</p>
						<div
							role="radiogroup"
							aria-labelledby={themeGroupLabelId}
							className="grid grid-cols-3 gap-2"
						>
							{themeOptions.map((option, index) => (
								<ThemeButton
									key={option.value}
									ref={(el) => {
										themeButtonRefs.current[index] = el
									}}
									active={theme === option.value}
									onClick={() => handleThemeChange(option.value)}
									onKeyDown={(e) => handleThemeKeyDown(e, index)}
									icon={option.icon}
									label={option.label}
								/>
							))}
						</div>
					</div>
				</div>
			</div>

			{/* Account - only show when logged in */}
			{session?.user && (
				<div className="settings-card">
					<h2>
						<User size={20} className="text-primary" />
						<span>{t('common.profile')}</span>
					</h2>

					<div className="space-y-4">
						<div className="flex items-center gap-3">
							{session.user.image ? (
								<img src={session.user.image} alt="" className="w-12 h-12 rounded-full" />
							) : (
								<div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
									<User size={24} className="text-primary" />
								</div>
							)}
							<div>
								<p className="font-semibold">
									{session.user.name || session.user.email?.split('@')[0]}
								</p>
								<p className="text-sm text-base-content/60">{session.user.email}</p>
							</div>
						</div>

						<Link
							to="/profile"
							className={cn(
								buttonClasses({ variant: 'ghost', size: 'sm' }),
								'text-sm flex items-center gap-2 border border-base-300 w-fit',
							)}
						>
							<User size={16} />
							{t('profile.title')}
						</Link>
					</div>
				</div>
			)}

			{/* About */}
			<div className="settings-card">
				<h2>
					<Info size={20} className="text-coral-500" />
					<span>{t('settings.about')}</span>
				</h2>

				<div className="space-y-4">
					<div className="flex items-center gap-3">
						<BrandMark size="lg" />
						<div>
							<p className="font-semibold">{t('settings.appName')}</p>
							<p className="text-sm text-base-content/60">{t('settings.version')}</p>
						</div>
					</div>

					<p className="text-sm text-base-content/70">{t('settings.appDescription')}</p>

					<div className="flex flex-wrap gap-3 pt-2">
						<a
							href="https://www.teiresias.muni.cz/polygraf"
							target="_blank"
							rel="noopener noreferrer"
							className={cn(
								buttonClasses({ variant: 'ghost', size: 'sm' }),
								'text-sm flex items-center gap-2 border border-base-300',
							)}
						>
							<Globe size={16} />
							{t('settings.webPolygraf')}
							<span className="sr-only"> {t('common.opensInNewTab')}</span>
							<ExternalLink size={14} className="text-base-content/40" />
						</a>
						<a
							href="https://www.muni.cz"
							target="_blank"
							rel="noopener noreferrer"
							className={cn(
								buttonClasses({ variant: 'ghost', size: 'sm' }),
								'text-sm flex items-center gap-2 border border-base-300',
							)}
						>
							<Globe size={16} />
							{t('settings.muniLink')}
							<span className="sr-only"> {t('common.opensInNewTab')}</span>
							<ExternalLink size={14} className="text-base-content/40" />
						</a>
					</div>
				</div>
			</div>
		</div>
	)
}

const ThemeButton = forwardRef<
	HTMLButtonElement,
	{
		active: boolean
		onClick: () => void
		onKeyDown: (e: React.KeyboardEvent) => void
		icon: React.ReactNode
		label: string
	}
>(function ThemeButton({ active, onClick, onKeyDown, icon, label }, ref) {
	return (
		// A native <input type="radio"> can't carry this card layout + roving
		// tabindex, so we implement the APG radio pattern on a <button>.
		// biome-ignore lint/a11y/useSemanticElements: styled radio uses button + role
		<button
			ref={ref}
			role="radio"
			aria-checked={active}
			// Roving tabindex: only the checked radio is in the tab order; arrow
			// keys move focus among the rest (APG radio pattern).
			tabIndex={active ? 0 : -1}
			onClick={onClick}
			onKeyDown={onKeyDown}
			className={`
				flex flex-col items-center gap-2 p-4 rounded-xl border transition-all duration-200
				${
					active
						? 'bg-primary text-primary-content border-primary shadow-soft'
						: 'bg-base-200 border-base-300 hover:bg-base-300'
				}
			`}
		>
			{icon}
			<span className="text-sm font-medium">{label}</span>
		</button>
	)
})
